import { Injectable } from '@angular/core';
import { AgGridFactory, AgGridNoRowsOverlayComponent, AgGridZeroStateMessages, LoadableStatus, TEXT_COLUMN } from '@anduin/shared';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { DateFormatterService } from 'src/app/shared/utils/date-formatter.service';
import { SENT_AT_COLUMN_ID, shouldSkipSentExportRow } from '../utils/sent-table-export-params';

interface EmailDeliveryHubGridFactoryParams<Data = unknown> {
  onGridReadyCallback: (event: GridReadyEvent<Data>) => void;
  getGridDataStatus: () => LoadableStatus;
  isExternalFilterPresent: () => boolean;
}

@Injectable()
export class EmailDeliveryHubGridOptionsFactory {
  constructor(
    private readonly gridFactory: AgGridFactory,
    private readonly dateFormatterService: DateFormatterService,
  ) {}

  create(params: EmailDeliveryHubGridFactoryParams): GridOptions {
    const baseOptions = this.gridFactory.serverSideGridOption({
      zeroStateMessages: getZeroStateMessages(),
      getGridDataStatus: () => params.getGridDataStatus(),
    });

    return {
      ...baseOptions,
      isExternalFilterPresent: () => params.isExternalFilterPresent(),
      animateRows: true,
      treeData: true,
      suppressRowHoverHighlight: true,
      /**
       * Deprecated in newer version of Ag Grid
       * but can easily replaced with
       * suppressServerSideInfiniteScroll: false,
       * when we update and that will behaves the same
       */
      serverSideStoreType: 'partial',
      defaultColDef: this._defaultColDef(),
      getRowClass: (params) => {
        if (params.node.level > 0) {
          return 'child-row';
        }
      },
      isServerSideGroup: (data) => {
        // Don't show expand button if there's only one child
        return data.hasChildren !== false && (!data.recipientDetails || data.recipientDetails.length > 1);
      },
      getServerSideGroupKey: (dataItem) => dataItem.id,
      autoGroupColumnDef: {
        colId: 'sentAt',
        headerName: 'SENT DATE',
        field: 'sentAt',
        pinned: 'left',
        lockPinned: true,
        width: 135,
        sort: 'desc',
        valueFormatter: ({ value }) => this.dateFormatterService.toDisplayFormat(value),
        cellRendererParams: {
          suppressCount: true,
        },
        cellClassRules: {
          'child-first-cell': (params) => params.node.level > 0,
        },
      },
      suppressRowClickSelection: true,
      defaultCsvExportParams: { shouldRowBeSkipped: shouldSkipSentExportRow },
      defaultExcelExportParams: { shouldRowBeSkipped: shouldSkipSentExportRow },
      pinnedTopRowData: [],
      pinnedBottomRowData: [],
      suppressNoRowsOverlay: false,
      noRowsOverlayComponent: AgGridNoRowsOverlayComponent,
      noRowsOverlayComponentParams: {
        headerRowsCount: 2,
        asyncMessage: baseOptions.context?.zeroStateOverlayMessage$,
      },
      onGridReady: params.onGridReadyCallback,
    };
  }

  createExportGridOptions(): GridOptions {
    return {
      ...this.gridFactory.serverSideGridOption(),
      defaultColDef: this._defaultColDef(),
      suppressRowClickSelection: true,
      defaultCsvExportParams: { shouldRowBeSkipped: shouldSkipSentExportRow },
      defaultExcelExportParams: { shouldRowBeSkipped: shouldSkipSentExportRow },
    };
  }

  createExportSentAtColumnDef(): ColDef {
    return {
      colId: SENT_AT_COLUMN_ID,
      field: 'sentAt',
      headerName: 'SENT DATE',
      width: 135,
      valueFormatter: ({ value }) => this.dateFormatterService.toDisplayFormat(value),
    };
  }

  private _defaultColDef(): ColDef {
    return {
      ...TEXT_COLUMN(),
      sortable: true,
      cellClassRules: {},
      headerClass: '',
      pinned: false,
      lockPinned: true,
      resizable: true,
      minWidth: 100,
    };
  }
}

function getZeroStateMessages(): AgGridZeroStateMessages {
  const getMessage = (header: string, body: string) => `
    <div class="d-flex flex-column align-items-center justify-content-center p-4 max-width-420">
      <h3 class="mb-2">${header}</h3>
      <p class="tc--gray-400">${body}</p>
    </div>
  `;

  return {
    empty: getMessage(
      'No emails to be shown',
      'Emails sent to Clients from invoices, payments, and collections will be listed here once they start being sent.',
    ),
    error: getMessage(
      'An error occurred',
      'There was an error retrieving records. Please try again.',
    ),
    filter: getMessage(
      'No emails to be shown',
      'Emails matching your selected criteria will be shown here. Try adjusting the filters above to find other emails.',
    ),
  };
}
